import {
  MessageSquareText, Phone, FileText, ShieldCheck, Megaphone, Clock, Users,
  BarChart3, Sparkles, Scale, CalendarCheck, Bell, Brain, Building2, Zap,
  BadgeCheck, Globe, Languages, ClipboardList, PenLine, Headset, Lock, Search,
} from "lucide-react";

/* ------------------------------------------------------------------ */
/*  COMPARISON PAGES  ( /compare/:slug )                               */
/*  Fair, non-defamatory framing: we name what each tool is great at, */
/*  then where communities pair or switch to Ask Redline. We integrate */
/*  with several of these, so "alternative" is framed honestly.        */
/* ------------------------------------------------------------------ */
export const COMPETITORS = [
  {
    slug: "townsq-alternative",
    competitor: "TownSq",
    keyword: "TownSq alternative",
    category: "Resident engagement portal",
    metaTitle: "TownSq Alternative for HOAs, Ask Redline AI Text & Call Concierge",
    metaDescription:
      "Looking for a TownSq alternative? Ask Redline answers residents instantly by text and phone, grounded in your governing documents, with no app to install. Compare features.",
    hero: {
      eyebrow: "TownSq alternative",
      title: "The TownSq alternative residents actually use, because it's just a text.",
      sub: "TownSq is a solid resident portal. But portals only help the residents who log in. Ask Redline meets every resident where they already are: one number they can text or call, answered instantly and grounded in your community's own documents.",
    },
    strengths: [
      "Announcements, community calendars and a resident feed",
      "Delivery and read tracking on official notices",
      "Tie-ins with several management-company back offices",
    ],
    gaps: [
      { title: "Portals depend on logins", body: "Adoption stalls when residents have to download an app and remember a password. Ask Redline works over plain SMS, MMS and WhatsApp, nothing to install, so participation is near-universal." },
      { title: "No instant, cited answers", body: "TownSq shows posts; it doesn't answer questions. Ask Redline reads your CC&Rs, bylaws and rules and replies in seconds with the exact section it used, 24/7, without a manager." },
      { title: "No live phone answering", body: "Residents still call the office. With the AI Voice Concierge, Ask Redline answers the phone live, grounded in the same documents, and logs requests automatically." },
    ],
    table: [
      ["Resident onboarding", "App download + account", "Just text or call a number"],
      ["Resident web portal", "Yes (app + login)", "Optional, no login or app"],
      ["Instant answers from your documents", false, "Cited, 24/7"],
      ["Answers live phone calls", false, "AI Voice Concierge"],
      ["Maintenance intake with photos", "Portal form", "By text or call, auto-routed"],
      ["Community-wide text alerts", "Push / email", "SMS + email, consent handled"],
      ["Works without changing your system of record", true, "Integrates with TownSq"],
    ],
    complement:
      "Prefer to keep TownSq as your system of record? You can. Ask Redline layers on as the resident-facing front door, it answers, captures intake and can hand the ticket to your portal, so you get instant text and phone coverage without ripping anything out.",
    faqs: [
      { q: "Is Ask Redline a replacement for TownSq?", a: "It can be either. Many communities run Ask Redline as their resident-facing concierge and keep their existing management portal as the back office, Ask Redline integrates with TownSq. Others move fully to Ask Redline's built-in requests, documents, events and reporting." },
      { q: "Do residents need an app like they do with TownSq?", a: "No. Ask Redline works over ordinary text messaging, MMS and WhatsApp, and residents can also call and speak to the concierge. There is no app to install and no login to manage." },
      { q: "Can Ask Redline answer questions about our rules automatically?", a: "Yes. It answers only from your uploaded governing documents and cites the exact section, so residents get the rule, not a guess, around the clock." },
    ],
  },
  {
    slug: "vantaca-alternative",
    competitor: "Vantaca",
    keyword: "Vantaca alternative",
    category: "Management-company operating system",
    metaTitle: "Vantaca Alternative, Ask Redline AI Resident Concierge for HOAs",
    metaDescription:
      "A lighter, resident-facing Vantaca alternative. Ask Redline gives every community an AI concierge that answers by text and phone, sold per community, with no back-office overhaul.",
    hero: {
      eyebrow: "Vantaca alternative",
      title: "All of the resident experience, none of the back-office overhaul.",
      sub: "Vantaca is a heavyweight operating system for large management companies. If what you actually need is instant, accurate resident communication, not a full accounting migration, Ask Redline delivers that in about a week, per community.",
    },
    strengths: [
      "Deep community-association accounting and reserve funds",
      "Workflow automation for professional management firms",
      "An AI roadmap aimed at back-office operations",
    ],
    gaps: [
      { title: "Built for the back office, not the resident's phone", body: "Vantaca's center of gravity is accounting and internal workflow. Ask Redline's is the resident conversation, instant answers, intake and alerts by text and voice." },
      { title: "Heavy to stand up", body: "Enterprise platforms mean migrations and long onboarding. Ask Redline provisions a line, digitizes your documents and goes live in about a week, community by community." },
      { title: "Not sold to individual HOAs", body: "Self-managed and single communities can't easily buy an enterprise system. Ask Redline is sold per community, from a single HOA to a 5,000-community portfolio." },
    ],
    table: [
      ["Time to go live", "Weeks to months", "~1 week per community"],
      ["Resident web portal", "Portal add-on", "Optional, no login or app"],
      ["Instant resident answers by text", false, "Cited, 24/7"],
      ["Live AI phone answering", false, "AI Voice Concierge"],
      ["Sold to a single/self-managed HOA", false, true],
      ["Sold per community, not per portfolio", "Enterprise contract", true],
      ["Portfolio dashboard for management companies", true, "Enterprise plan"],
    ],
    complement:
      "Keep Vantaca for accounting and back-office workflow. Ask Redline can sit in front of it as the AI resident layer, answering, triaging and routing requests, so residents get instant service while your financial system of record stays put.",
    faqs: [
      { q: "Is Ask Redline an accounting system like Vantaca?", a: "No, and that's deliberate. Ask Redline focuses on resident communication: instant cited answers, maintenance intake, alerts, events and reporting. It integrates alongside your accounting platform rather than replacing it." },
      { q: "We're a management company. Can Ask Redline scale across our portfolio?", a: "Yes. The Enterprise plan supports unlimited communities and numbers, a portfolio dashboard with team roles, and portfolio terms, each community fully isolated." },
      { q: "How fast can a community go live versus an enterprise rollout?", a: "Most communities are live in about a week. We provision the number, digitize documents (even old scans) for free, load the roster and tune the assistant's voice." },
    ],
  },
  {
    slug: "cinc-systems-alternative",
    competitor: "CINC Systems",
    keyword: "CINC Systems alternative",
    category: "Enterprise management platform",
    metaTitle: "CINC Systems Alternative, Modern AI Concierge for Communities",
    metaDescription:
      "A modern CINC Systems alternative focused on the resident experience. Ask Redline answers by text and phone with cited answers from your documents, live in about a week.",
    hero: {
      eyebrow: "CINC Systems alternative",
      title: "A modern resident layer for teams tired of dated portals.",
      sub: "CINC has decades of back-office depth. But residents don't experience your accounting engine, they experience how fast they get an answer. Ask Redline makes that instant, and it feels like 2026, not 2006.",
    },
    strengths: [
      "Long-established accounting and banking integrations",
      "Resident portals and ARC workflows for management firms",
      "Deep operational reporting for professional staff",
    ],
    gaps: [
      { title: "Dated resident experience", body: "Legacy portals frustrate residents. Ask Redline's interface for residents is the text thread they already use, plus a clean, no-install web portal when they want one." },
      { title: "No agentic AI answering", body: "Ask Redline reads your documents and answers autonomously, learns the questions residents keep asking, and hands your board a one-tap card to teach it." },
      { title: "Complex for smaller communities", body: "Enterprise complexity is overkill for many boards. Ask Redline is simple, per-community, and live in about a week." },
    ],
    table: [
      ["Resident-facing AI answers", false, "Cited, 24/7"],
      ["Live phone answering by AI", false, "AI Voice Concierge"],
      ["No app required for residents", "Portal login", true],
      ["Self-improving knowledge base", false, "Learns from real questions"],
      ["Resident web portal", "Legacy portal login", "Modern, optional, no install"],
      ["Time to launch a community", "Long onboarding", "~1 week"],
      ["Integrates with existing back office", true, "TownSq, Vantaca, AppFolio"],
    ],
    complement:
      "You don't have to leave CINC to modernize the resident experience. Ask Redline drops in as the AI concierge on the front line and passes structured requests back to your platform.",
    faqs: [
      { q: "Will we lose our accounting if we add Ask Redline?", a: "No. Ask Redline is a communication and intake layer. Keep your accounting platform; Ask Redline handles the resident conversation and routes requests to it." },
      { q: "How is the AI different from a chatbot?", a: "It only answers from your verified governing documents and cites the source, declines legal/financial advice, and escalates to a human when needed, built for HOAs, not generic chat." },
      { q: "Is it easier to roll out than an enterprise system?", a: "Considerably. There's no migration, we provision a number, digitize your documents and load your roster, and the community is live in about a week." },
    ],
  },
  {
    slug: "payhoa-alternative",
    competitor: "PayHOA",
    keyword: "PayHOA alternative",
    category: "Self-managed HOA software",
    metaTitle: "PayHOA Alternative, AI Text & Call Concierge for Self-Managed HOAs",
    metaDescription:
      "A PayHOA alternative for boards that want instant resident communication. Ask Redline answers by text and phone from your documents, no app, no logins for residents.",
    hero: {
      eyebrow: "PayHOA alternative",
      title: "For self-managed boards that want the questions to stop.",
      sub: "PayHOA is popular for dues and accounting. But volunteer boards burn out answering the same resident questions by email. Ask Redline answers them automatically, by text and phone, so your board gets its evenings back.",
    },
    strengths: [
      "Online dues collection and financial reporting",
      "Violation logging with photos",
      "Straightforward setup for smaller associations",
    ],
    gaps: [
      { title: "Communication is email + portal, not conversational", body: "Residents want to text. Ask Redline answers instantly over SMS and phone, grounded in your documents, so nobody waits on a portal message." },
      { title: "No AI concierge or voice line", body: "PayHOA doesn't answer resident questions for you. Ask Redline does, 24/7, and even answers the phone with the AI Voice Concierge." },
      { title: "Knowledge walks out with the board", body: "Every answer, request and decision in Ask Redline becomes permanent community memory that survives board turnover." },
    ],
    table: [
      ["Instant answers to resident questions", false, "Cited, 24/7"],
      ["Answers phone calls", false, "AI Voice Concierge"],
      ["No app/login for residents", "Portal login", true],
      ["Maintenance intake by text with photos", "Portal form", true],
      ["Dues & accounting", true, "Pairs with your accounting tool"],
      ["Resident web portal", "Portal login", "Optional, no login or app"],
      ["Built for a single self-managed HOA", true, true],
    ],
    complement:
      "Love PayHOA for payments? Keep it. Add Ask Redline as the resident concierge so your board stops answering the same questions, the two sit happily side by side.",
    faqs: [
      { q: "Does Ask Redline collect dues like PayHOA?", a: "Ask Redline focuses on resident communication and intake, not accounting. Many boards keep their payment tool and add Ask Redline for instant answers and request handling." },
      { q: "Does this work for a small, self-managed HOA?", a: "Yes, it's sold to single communities, not just management companies, and every community gets the AI concierge plus the command center behind it. Book a demo and we'll put a proposal together for your community." },
      { q: "What do residents have to install?", a: "Nothing. They text or call one number. There's also an optional no-install web portal for documents and requests." },
    ],
  },
  {
    slug: "frontsteps-alternative",
    competitor: "FrontSteps",
    keyword: "FrontSteps alternative",
    category: "Enforcement & access platform",
    metaTitle: "FrontSteps Alternative, AI Resident Concierge & Communication",
    metaDescription:
      "A FrontSteps alternative centered on resident communication. Ask Redline answers by text and phone, logs requests, and sends community-wide alerts, no app required.",
    hero: {
      eyebrow: "FrontSteps alternative",
      title: "Enforcement is one job. Answering residents is the other.",
      sub: "FrontSteps is strong on violations, ARC and access control. Ask Redline owns the everyday conversation, the questions, requests and alerts that flood your team, and does it over text and phone with cited answers.",
    },
    strengths: [
      "Violation and architectural-review audit trails",
      "Security, visitor and access-control features",
      "A mobile resident experience",
    ],
    gaps: [
      { title: "Residents want answers, not another app", body: "Ask Redline works over plain text and phone. Residents get instant, cited answers without opening anything." },
      { title: "No autonomous AI answering", body: "Ask Redline reads your documents and answers 24/7, runs guided intake with Fair-Housing guardrails, and escalates to a human when needed." },
      { title: "Institutional memory", body: "Every question and decision becomes permanent community knowledge that outlives any board or manager." },
    ],
    table: [
      ["AI answers from your documents", false, "Cited, 24/7"],
      ["Live AI phone answering", false, "AI Voice Concierge"],
      ["No-install for residents", "App", true],
      ["Community-wide text + email alerts", "Notifications", "Consent handled"],
      ["Violation / ARC enforcement", true, "Requests & tracking"],
      ["Resident web portal", "Mobile app", "Optional, no login or app"],
      ["Free document digitization at onboarding", false, true],
    ],
    complement:
      "Keep FrontSteps for enforcement and access. Ask Redline complements it as the AI concierge that fields the day-to-day resident conversation and routes anything actionable to your team.",
    faqs: [
      { q: "Does Ask Redline do violation and ARC tracking?", a: "Ask Redline captures and tracks resident requests and can route enforcement items to your team; deep ARC/enforcement suites like FrontSteps can remain your system of record while Ask Redline handles communication." },
      { q: "How does it handle emergencies?", a: "Life-threatening situations are always directed to 911, and the platform can broadcast a community-wide safety alert in seconds. This guardrail can't be turned off." },
      { q: "Do residents need the app?", a: "No, text or call is enough. An optional no-install web portal is available for documents and requests." },
    ],
  },
  {
    slug: "appfolio-alternative",
    competitor: "AppFolio",
    keyword: "AppFolio alternative",
    category: "Property management software",
    metaTitle: "AppFolio Alternative for HOAs, Ask Redline AI Concierge",
    metaDescription:
      "An AppFolio alternative purpose-built for HOAs and associations. Ask Redline answers residents by text and phone from your governing documents, community by community.",
    hero: {
      eyebrow: "AppFolio alternative",
      title: "Purpose-built for communities, not rental portfolios.",
      sub: "AppFolio is a broad property-management suite. For HOAs and associations that mainly need instant, accurate resident communication grounded in governing documents, Ask Redline is the focused, faster-to-launch choice.",
    },
    strengths: [
      "Broad property-management and accounting features",
      "Resident/tenant portals and payments",
      "Some AI leasing automation",
    ],
    gaps: [
      { title: "Association-first, not rental-first", body: "Ask Redline is built around CC&Rs, bylaws, boards and residents, the HOA world, not rental operations." },
      { title: "Conversational by default", body: "Residents text or call and get cited answers instantly; there's no portal to learn." },
      { title: "Fast, per-community rollout", body: "Live in about a week per community, with free document digitization included." },
    ],
    table: [
      ["Built specifically for HOAs/associations", "General PM", true],
      ["Instant cited answers to residents", false, "24/7"],
      ["AI phone answering", "Leasing-focused", "AI Voice Concierge"],
      ["No app required for residents", "Portal login", true],
      ["Governing-document knowledge base", false, "Cited from CC&Rs/bylaws"],
      ["Resident web portal", "Portal login", "Optional, no login or app"],
      ["Integrates with AppFolio", ", ", true],
    ],
    complement:
      "Running AppFolio for the portfolio back office? Ask Redline integrates as the resident-facing AI concierge so associations get instant text and phone service on top of it.",
    faqs: [
      { q: "Is Ask Redline a full property-management system?", a: "No. It's a resident-communication and intake platform for HOAs and associations. It integrates with property-management systems like AppFolio rather than replacing accounting." },
      { q: "Why choose a specialized tool over a broad suite?", a: "Because residents feel communication, not accounting. A focused concierge that cites your governing documents drives adoption and cuts the repetitive questions your team fields." },
      { q: "Can a single community buy it, or is it management-company only?", a: "Either. It's sold per community, one self-managed HOA or a whole portfolio, with an Enterprise plan for management companies running many communities." },
    ],
  },
];

export const getCompetitor = (slug) => COMPETITORS.find((c) => c.slug === slug);

/* ------------------------------------------------------------------ */
/*  SOLUTIONS PAGES  ( /solutions/:slug )                              */
/* ------------------------------------------------------------------ */
export const SOLUTIONS = [
  {
    slug: "hoa-communication-software",
    keyword: "HOA communication software",
    metaTitle: "HOA Communication Software, AI Text & Call Concierge | Ask Redline",
    metaDescription:
      "Ask Redline is HOA communication software that answers residents instantly by text and phone, sends community-wide alerts, and grounds every answer in your governing documents.",
    hero: {
      eyebrow: "HOA communication software",
      title: "The HOA communication software residents actually respond to.",
      sub: "Stop chasing residents through portals and email blasts. Ask Redline is two-way HOA communication over the channel every resident already uses, text and phone, with instant, cited answers and consent-safe broadcasts.",
    },
    intro: [
      "Most HOA communication tools are one-directional: post an announcement and hope residents log in to read it. Ask Redline flips that. Residents text or call one dedicated number and get an instant, accurate answer pulled straight from your community's governing documents, and when the board needs to reach everyone, a single broadcast lands in every opted-in resident's texts in seconds.",
      "Because it works over ordinary SMS, MMS and WhatsApp, there's nothing to install and no login to forget, so participation is near-universal, including residents who never touch an app.",
    ],
    features: [
      { icon: MessageSquareText, title: "Two-way texting", body: "Residents ask in their own words and get cited answers 24/7, no portal, no app, no hold music." },
      { icon: Phone, title: "Live phone answering", body: "The AI Voice Concierge answers calls live, grounded in the same documents, and logs requests." },
      { icon: Megaphone, title: "Consent-safe broadcasts", body: "Push safety and operations alerts to every opted-in resident by text and email, with STOP/HELP handled automatically." },
      { icon: FileText, title: "Grounded in your documents", body: "Every policy answer quotes the exact CC&R, bylaw or rule section, never a guess." },
      { icon: Bell, title: "Emergency alerts", body: "Burst pipe or downed gate? Broadcast a community-wide alert in seconds; true emergencies always route to 911." },
      { icon: BarChart3, title: "Board-ready reporting", body: "Show exactly what was asked, answered and resolved each month, proof of value for the board." },
    ],
    outcomes: [
      "Cut repetitive resident questions off your team's plate",
      "Reach every resident on the channel they actually use",
      "Keep a timestamped, exportable consent log for TCPA/A2P compliance",
      "Give residents same-second answers instead of next-day replies",
    ],
    faqs: [
      { q: "How is this different from a resident portal?", a: "Portals only help residents who log in. Ask Redline answers over text and phone, no app or login, so far more residents actually engage, and it answers questions instead of just posting notices." },
      { q: "Is HOA texting legal?", a: "Yes, with proper consent. Ask Redline is built for TCPA and A2P 10DLC compliance: clear opt-in language, required disclosures, automatic STOP/HELP/START handling, and an exportable consent log." },
      { q: "Can we still send broadcasts to everyone?", a: "Yes. Send one message to every opted-in resident by text and email, with consent and opt-outs handled for you." },
    ],
    related: ["ai-answering-service-property-management", "self-managed-hoa-software", "hoa-management-companies"],
  },
  {
    slug: "ai-answering-service-property-management",
    keyword: "AI answering service for property management",
    metaTitle: "AI Answering Service for Property Management & HOAs | Ask Redline",
    metaDescription:
      "A 24/7 AI answering service for property management and HOAs. Ask Redline answers resident calls and texts live, triages emergencies, and logs work orders automatically.",
    hero: {
      eyebrow: "AI answering service",
      title: "A 24/7 AI answering service built for communities.",
      sub: "Never send a resident to voicemail again. Ask Redline answers calls and texts live, day, night, weekends and event surges, with accurate answers from your documents and automatic request logging.",
    },
    intro: [
      "Traditional answering services read a script and take a message. Ask Redline actually helps: it answers the resident's question from your governing documents, opens and routes a maintenance request, or triages an emergency, then hands your team a clean, structured record.",
      "It's a flat monthly service, not per-minute billing, so it handles a quiet Tuesday and a storm-night surge the same way, without a runaway bill.",
    ],
    features: [
      { icon: Phone, title: "Live call answering", body: "Residents call your community number and speak to the AI in real time, it reads details back before logging anything." },
      { icon: MessageSquareText, title: "Text + WhatsApp", body: "The same concierge handles SMS, MMS and WhatsApp, so residents choose how they reach you." },
      { icon: ShieldCheck, title: "Emergency triage", body: "Detects true emergencies, directs residents to 911, and alerts your on-call team instantly." },
      { icon: ClipboardList, title: "Automatic work orders", body: "Turns a call or text into a tracked, assigned request, with photos when residents send them." },
      { icon: Clock, title: "After-hours coverage", body: "24/7 by default, or configure coverage hours per community." },
      { icon: Languages, title: "Multilingual", body: "Auto-detects and replies in each resident's language across text and voice." },
    ],
    outcomes: [
      "Zero missed after-hours calls or voicemails",
      "No per-minute answering fees, however busy the month gets",
      "Every call and text captured as a structured, searchable record",
      "Emergencies escalated to a human in seconds",
    ],
    faqs: [
      { q: "Does a real person ever get involved?", a: "Yes. You can add a live Community Concierge Team so a real person steps into a text thread on your coverage hours, and emergencies always alert your on-call staff." },
      { q: "How is this better value than a call center?", a: "It's software, billed per community rather than per minute, so a storm or an event that triples your call volume doesn't triple the bill, and every answer is cited to your documents instead of read off a script." },
      { q: "Will it forward or take a message if needed?", a: "Yes. A voicemail-and-forward safety net always sits underneath the AI, and you can set a monthly voice-minute cap per community." },
    ],
    related: ["hoa-communication-software", "hoa-management-companies", "condo-association-software"],
  },
  {
    slug: "self-managed-hoa-software",
    keyword: "self-managed HOA software",
    metaTitle: "Self-Managed HOA Software, AI Concierge for Volunteer Boards",
    metaDescription:
      "Self-managed HOA software that answers residents for you. Ask Redline gives volunteer boards a 24/7 AI text & call concierge, request tracking and alerts, live in about a week.",
    hero: {
      eyebrow: "For self-managed HOAs",
      title: "Run your HOA like it has a full-time office, without one.",
      sub: "Volunteer boards don't have time to answer the same questions every night. Ask Redline answers them automatically, tracks requests, and keeps a permanent record so knowledge doesn't leave when the board turns over.",
    },
    intro: [
      "Self-managed communities punch above their weight when residents get instant answers and nothing falls through the cracks. Ask Redline handles the front line, pool hours, dues questions, parking rules, maintenance reports, so your board spends its volunteer time on decisions, not data entry.",
      "Setup is done for you: we provision the number, digitize your documents (even old scans, for free), load your roster and tune the assistant. Most self-managed communities are live in about a week.",
    ],
    features: [
      { icon: MessageSquareText, title: "Answers residents 24/7", body: "Cited answers from your CC&Rs and rules, so the board isn't the help desk." },
      { icon: ClipboardList, title: "Request tracking", body: "Every maintenance report becomes a tracked ticket, assigned, prioritized, closed." },
      { icon: Brain, title: "Permanent memory", body: "Decisions, vendors and answers stay on record when board members roll off." },
      { icon: Megaphone, title: "One-tap broadcasts", body: "Reach every resident by text and email with consent handled for you." },
      { icon: CalendarCheck, title: "Events & reservations", body: "Residents RSVP and book amenities by text; the board publishes once." },
      { icon: Zap, title: "Live in ~1 week", body: "We do the setup and document digitization for you, no IT project." },
    ],
    outcomes: [
      "Give your volunteer board its evenings back",
      "Never lose institutional knowledge to board turnover",
      "Sized for smaller communities, not enterprise portfolios",
      "Look and operate like a professionally managed community",
    ],
    faqs: [
      { q: "Is this workable for a small HOA?", a: "Yes, a single community can buy it on its own, and it includes the AI concierge and the community command center. There's no per-user or per-seat charge for a single community." },
      { q: "We're volunteers with no IT help. Is setup hard?", a: "No. Onboarding is white-glove, we provision your line, digitize your documents for free, load the roster and tune the assistant, then you invite residents with a link, QR code or text." },
      { q: "What happens when our board changes?", a: "Everything the concierge has learned and recorded stays. The next board inherits every answer, decision and vendor on day one." },
    ],
    related: ["hoa-communication-software", "ai-answering-service-property-management", "condo-association-software"],
  },
  {
    slug: "condo-association-software",
    keyword: "condo association software",
    metaTitle: "Condo Association Software, AI Resident Concierge | Ask Redline",
    metaDescription:
      "Condo (COA) association software that answers owners and renters instantly by text and phone, handles amenity bookings and alerts, and grounds answers in your governing documents.",
    hero: {
      eyebrow: "For condos & COAs",
      title: "Condo living has a lot of questions. Now they answer themselves.",
      sub: "Amenity hours, guest parking, move-in rules, package policies, quiet hours, condo residents ask constantly. Ask Redline answers instantly by text and phone, and books amenities without a phone tag.",
    },
    intro: [
      "High-density condo communities generate a steady stream of resident questions and requests. Ask Redline fields them 24/7 with answers cited to your declaration, bylaws and house rules, for owners and renters alike, and turns maintenance reports into tracked work orders with photos.",
      "It respects household privacy and verifies identity before sharing anything account-specific, so a renter never sees an owner's information and vice versa.",
    ],
    features: [
      { icon: CalendarCheck, title: "Amenity reservations", body: "Residents book the pool, gym, elevator or clubhouse by text, no front-desk phone tag." },
      { icon: FileText, title: "House-rules answers", body: "Cited answers on parking, pets, quiet hours, move-ins and packages, 24/7." },
      { icon: Phone, title: "Front-desk relief", body: "The AI Voice Concierge answers calls live, so staff aren't tied to the phone." },
      { icon: ShieldCheck, title: "Security reporting", body: "Residents flag concerns by text or photo; optional live monitoring can respond in real time." },
      { icon: Lock, title: "Private by design", body: "Identity verified before anything account-specific is shared; owners and renters kept separate." },
      { icon: Megaphone, title: "Building-wide alerts", body: "Elevator down or water shutoff? One broadcast reaches every unit instantly." },
    ],
    outcomes: [
      "Free the front desk from repetitive calls",
      "Book amenities without staff involvement",
      "Serve owners and renters without mixing up households",
      "Instant building-wide alerts when something breaks",
    ],
    faqs: [
      { q: "Does it work for both owners and renters?", a: "Yes. The concierge recognizes each resident, keeps households private, and verifies identity before sharing anything account-specific." },
      { q: "Can residents book amenities?", a: "Yes. With the amenities module, residents reserve shared spaces by text and the concierge confirms availability automatically." },
      { q: "Is our building's data separate from other communities?", a: "Completely. Strict multi-tenant isolation means your documents, roster and conversations are walled off from every other community." },
    ],
    related: ["hoa-communication-software", "ai-answering-service-property-management", "hoa-management-companies"],
  },
  {
    slug: "hoa-management-companies",
    keyword: "HOA management company software",
    metaTitle: "Software for HOA Management Companies, Portfolio AI Concierge",
    metaDescription:
      "Scale resident communication across your whole portfolio. Ask Redline gives HOA management companies an AI concierge per community, a portfolio dashboard and terms that scale.",
    hero: {
      eyebrow: "For management companies",
      title: "One manager, more doors, zero dropped calls.",
      sub: "Give every community in your portfolio a 24/7 AI concierge that answers residents by text and phone, then watch the repetitive workload lift off your managers across the whole book of business.",
    },
    intro: [
      "For management companies, resident communication is the workload that never ends. Ask Redline standardizes it across every community: each gets its own number, documents, roster and cited answers, all held to the same auditable standard, and all visible from one portfolio dashboard.",
      "An independent analysis of 977 real resident requests found roughly 92% could be fully resolved or substantially prepared by AI. Multiply that across your portfolio and you scale service without scaling headcount.",
    ],
    features: [
      { icon: Building2, title: "Portfolio dashboard", body: "Every community in one view, each fully isolated, with team roles and cross-community intelligence." },
      { icon: BarChart3, title: "Portfolio intelligence", body: "Emerging topics, escalation reasons and trends across your whole book of business." },
      { icon: BadgeCheck, title: "Cited & auditable", body: "Every answer stored with its source, defensible for boards and attorneys." },
      { icon: Users, title: "Per-community logins & roles", body: "Managers, board members and operators get exactly the access they need." },
      { icon: Globe, title: "White-labeling", body: "Each community gets its own branded front door and dedicated line." },
      { icon: Zap, title: "Portfolio terms", body: "Enterprise terms that scale from five communities to five thousand." },
    ],
    outcomes: [
      "Cover more doors per manager without adding headcount",
      "Standardize a cited, auditable answer across every community",
      "Spot portfolio-wide trends before they become escalations",
      "Onboard new communities in about a week each",
    ],
    faqs: [
      { q: "Can we manage many communities from one place?", a: "Yes. The Enterprise plan includes a portfolio dashboard with unlimited communities and numbers, team roles, portal integrations and portfolio terms, each community fully isolated." },
      { q: "How does it help our managers specifically?", a: "It removes the repetitive questions, incomplete requests and administrative back-and-forth, an analysis of 977 requests showed ~92% is AI-addressable, so managers focus on decisions, vendors and residents." },
      { q: "Does it integrate with our existing platforms?", a: "Yes, integrations are available for TownSq, Vantaca and AppFolio, with more on the roadmap, so Ask Redline sits in front of your system of record." },
    ],
    related: ["ai-answering-service-property-management", "hoa-communication-software", "condo-association-software"],
  },
];

export const getSolution = (slug) => SOLUTIONS.find((s) => s.slug === slug);

/* ------------------------------------------------------------------ */
/*  GUIDES / BLOG  ( /resources, /resources/:slug )                   */
/*  Bodies are markdown, rendered with react-markdown + prose.         */
/* ------------------------------------------------------------------ */
export const GUIDES = [
  {
    slug: "how-to-choose-hoa-management-software",
    title: "How to Choose HOA Management Software (2026 Buyer's Guide)",
    description:
      "A practical framework for evaluating HOA and community-association software in 2026, from accounting and communication to AI and compliance, without buying more than you need.",
    category: "Buyer's guide",
    date: "2026-05-12",
    readMins: 8,
    icon: Search,
    body: `## Start with the job, not the feature list

The biggest mistake boards and management companies make is shopping for features before defining the job. HOA software spans four very different jobs, and almost no single tool is best at all four:

- **Governance**, meetings, votes, documents, e-signatures.
- **Accounting**, dues, reserves, bank reconciliation, reporting.
- **Resident experience**, how residents get answers, make requests and stay informed.
- **Enforcement & compliance**, violations, ARC reviews, TCPA/A2P for messaging.

Decide which job is your biggest pain *this year*, and weight your evaluation accordingly.

## Match the tool tier to who you are

Self-managed boards and professional management companies use different tiers:

- **Self-managed / volunteer boards** should prioritize simplicity and near-zero resident friction. Enterprise platforms are usually overkill and often aren't even sold to individual HOAs.
- **Management companies** need workflow density, portfolio dashboards, team roles and portfolio-wide controls.

If a demo assumes a full-time back office and a multi-month migration, ask whether that matches your reality.

## The resident-experience gap most tools ignore

Accounting engines don't fail communities, communication does. Residents judge you on how fast they get an answer, not on your reserve-study module. Yet most platforms bury communication inside a portal that only a fraction of residents ever log into.

When you evaluate, ask:

1. What percentage of residents actually adopt the tool? (Portals that require an app and login typically see low participation.)
2. Can residents get an **instant, accurate answer** to a policy question without a human?
3. Is there a **phone** path, not just a portal? Many residents still call.
4. Are answers **cited to your governing documents**, or is it a generic chatbot that can invent a rule?

## Don't skip compliance

If you text residents, you're subject to the **TCPA** and carrier **A2P 10DLC** rules. Confirm any tool captures clear, timestamped consent, includes the required disclosures, and honors STOP/HELP/START automatically. Getting this wrong risks carrier filtering and legal exposure. (See our [HOA texting compliance guide](/resources/hoa-texting-tcpa-a2p-10dlc-compliance).)

## A simple scoring rubric

Score each shortlisted tool 1–5 on: resident adoption, instant answers, phone coverage, document-grounded accuracy, compliance, onboarding speed, and total cost per community. Weight resident experience heavily, it's the part your homeowners actually feel.

## Where Ask Redline fits

Ask Redline is deliberately focused on the resident-experience job: an AI concierge that answers by **text and phone**, cites your governing documents, logs requests, and sends consent-safe broadcasts, while integrating with the accounting or management platform you already run. Most communities are live in about a week.

Ready to see it on your documents? [Book a demo](/#contact).`,
    faqs: [
      { q: "What's the difference between HOA software for self-managed boards and management companies?", a: "Self-managed boards need simple, low-friction tools; management companies need portfolio dashboards, team roles and portfolio-wide controls. Many enterprise platforms aren't sold to individual HOAs at all." },
      { q: "Do we need one tool for everything?", a: "Rarely. It's common and often better to pair a focused resident-communication tool with your existing accounting platform, rather than forcing one suite to do every job poorly." },
    ],
  },
  {
    slug: "hoa-texting-tcpa-a2p-10dlc-compliance",
    title: "HOA Texting Compliance: TCPA and A2P 10DLC, Explained",
    description:
      "What HOAs and management companies must know before texting residents, consent, disclosures, STOP/HELP handling and A2P 10DLC registration, in plain English.",
    category: "Compliance",
    date: "2026-04-28",
    readMins: 7,
    icon: ShieldCheck,
    body: `## Why texting residents is regulated

Group texting to residents is governed by the **TCPA** (a federal law), the **CTIA** messaging guidelines, and enforced by carriers through **A2P 10DLC** registration. In plain terms: residents must knowingly agree before you text them, be told what they'll receive and how often, and always be able to stop.

Getting this right protects your community from complaints, message filtering by carriers, and legal exposure.

## The three things you must get right

1. **Consent**, Residents must opt in knowingly. Consent should be captured with a timestamp and stored where you can export it for an audit trail.
2. **Disclosure**, At opt-in, include the required language: what messages are about, that "message frequency varies," and that "message & data rates may apply," plus a link to your privacy policy and terms.
3. **Opt-out**, STOP must stop messages immediately; HELP must return help info; START must re-subscribe. These keywords are effectively mandatory.

## Good ways to collect opt-in

- A per-community **opt-in page** (link or QR code) posted at the mailroom, clubhouse or gate.
- **Texting your number**, the resident gets a one-time confirmation explaining how to opt out.
- Your **HOA roster**, where documented consent is captured and dated per household.

Whatever the source, store each opt-in as a timestamped, exportable consent record.

## A2P 10DLC in one paragraph

Carriers require business/application-to-person texting to be registered: your brand and your campaign (what you send and why). Registration improves deliverability and is essentially required to text at any scale from a standard number. A good platform handles this registration for you.

## Common mistakes

- Treating a roster of phone numbers as consent. It isn't, consent must be knowing and documented.
- Burying disclosures or omitting "message frequency varies / rates may apply."
- Manually handling STOP requests (they must be automatic and immediate).
- Reusing one number across many communities without proper campaign registration.

## How Ask Redline handles it

Ask Redline is built for this end to end: separate, clear consent language at every opt-in point; the required disclosures; automatic STOP/HELP/START handling; a privacy policy stating numbers are never sold or shared for marketing; and a complete, exportable log of who consented, when and how, plus A2P registration handled for you.

Want the compliant setup done for you? [Talk to us](/#contact).`,
    faqs: [
      { q: "Is a resident roster the same as consent?", a: "No. A list of phone numbers is not consent. Under the TCPA, residents must knowingly agree to receive texts, and that agreement should be documented with a timestamp." },
      { q: "Do we have to handle STOP and HELP ourselves?", a: "No, and you shouldn't. STOP, HELP and START must be honored automatically and immediately. Ask Redline handles these keywords for you and logs every opt-out." },
    ],
  },
  {
    slug: "how-to-reduce-hoa-resident-complaints",
    title: "How to Reduce HOA Resident Complaints (Without Adding Staff)",
    description:
      "Most HOA complaints come from slow answers and inconsistent information. Here's a practical playbook to cut complaint volume using faster, cited, 24/7 communication.",
    category: "Operations",
    date: "2026-03-30",
    readMins: 6,
    icon: MessageSquareText,
    body: `## Complaints are usually a symptom of slow, inconsistent answers

When we analyzed 977 real resident requests from a live community, two patterns jumped out: only about **42%** got a same-day answer, and nearly **39%** of residents had to message again because the first reply wasn't enough. Slow and incomplete answers are complaint factories.

The fix isn't more staff, it's faster, more consistent communication.

## 1. Make answers instant and available 24/7

A resident who gets an accurate answer in seconds rarely escalates. An AI concierge that answers by text and phone around the clock removes the wait entirely, including nights, weekends and event surges when the office is closed.

## 2. Make every answer consistent and cited

Complaints spike when two residents get two different answers. Ground every policy answer in your governing documents and cite the exact section, so everyone gets the same defensible answer, and disputes drop.

## 3. Close the loop on requests

"I reported it and never heard back" is one of the most common complaints. Turn every report into a tracked ticket that's assigned, prioritized and closed, with status updates back to the resident.

## 4. Get ahead of it with proactive alerts

Many complaints are really "nobody told me." When something affects everyone, a water shutoff, a gate outage, a schedule change, a single broadcast to every resident's phone prevents a wave of calls.

## 5. Fix the gaps the data shows you

Track the questions the assistant couldn't answer. Each one is a document to upload or a one-sentence note to add, and the assistant remembers it permanently, so that gap never causes a complaint again.

## The compounding effect

Every answered question, closed request and clarified rule makes the next interaction smoother. Communities that communicate this way see complaint volume fall and resident satisfaction climb, without adding headcount.

Curious what this looks like on your community's documents? [Book a demo](/#contact).`,
    faqs: [
      { q: "Will AI answering frustrate residents more?", a: "Not when it's accurate and honest. Ask Redline only answers from your verified documents, says so when it isn't sure, and hands off to a human for anything sensitive, which residents prefer to slow or inconsistent replies." },
      { q: "What's the fastest lever to cut complaints?", a: "Instant, consistent answers plus closing the loop on requests. Together they remove the two biggest complaint drivers: waiting and being ignored." },
    ],
  },
  {
    slug: "hoa-emergency-notification-system",
    title: "Building an HOA Emergency Notification System That Actually Reaches People",
    description:
      "A guide to HOA emergency and mass-notification systems: channels that residents actually see, escalation ladders, and how to avoid alert fatigue.",
    category: "Safety",
    date: "2026-03-10",
    readMins: 6,
    icon: Bell,
    body: `## The only metric that matters: did residents see it?

An emergency notification system is worthless if the alert lands somewhere residents don't check. Email gets buried. Portal notifications require the app. The one channel nearly everyone sees within minutes is **text**, which is why it should be the backbone of any HOA emergency system.

## Design for reach first

- **Text as primary.** SMS has the highest open rate of any channel and needs no app.
- **Email as backup.** Good for detail and record-keeping, not for speed.
- **Voice for the critical few.** For on-call responders, an automated phone call cuts through when a text might be missed.

## Use an escalation ladder for true emergencies

For incidents that need a human response, a burst main, a security threat, a flat blast isn't enough. Build a ladder:

1. Alert the first responder (e.g., on-call manager) by text and call.
2. If no acknowledgment within a set window, escalate to the next tier (e.g., board).
3. If still no ack, last-resort blast to all verified responders and log everything.

Every step should be time-stamped and require an explicit acknowledgment, so you know help is actually on the way.

## Always route life-safety to 911

No community system replaces emergency services. Any resident-facing assistant should immediately direct life-threatening situations to **911** and log the incident, a guardrail that should never be disabled.

## Avoid alert fatigue

If every minor notice is an "alert," residents tune out. Reserve broadcasts for things that genuinely affect everyone, and keep routine updates in normal channels. Consent and easy opt-out also keep your list healthy.

## How Ask Redline does it

Ask Redline sends community-wide alerts by text and email in seconds, with consent handled. For true emergencies, it runs a configurable escalation ladder, auto-calling responders, advancing tiers on no-acknowledgment, and a last-resort blast, while always routing life-safety to 911.

Want to map an escalation ladder for your community? [Book a demo](/#contact).`,
    faqs: [
      { q: "Why text instead of a portal notification for emergencies?", a: "Text has the highest open rate and needs no app or login, so it reaches the most residents fastest. Portal notifications only reach residents who have the app installed and open it." },
      { q: "Does an HOA emergency system replace 911?", a: "Never. A community system coordinates residents and staff, but life-threatening situations must always go to 911 first. Ask Redline enforces this as a guardrail that can't be turned off." },
    ],
  },
];

export const getGuide = (slug) => GUIDES.find((g) => g.slug === slug);

// Everything that should appear in sitemap.xml / internal linking.
export const ALL_SEO_PATHS = [
  ...COMPETITORS.map((c) => `/compare/${c.slug}`),
  ...SOLUTIONS.map((s) => `/solutions/${s.slug}`),
  ...GUIDES.map((g) => `/resources/${g.slug}`),
];
